import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { money } from '../../lib/format';
import { useInvestorScope } from '../../store/selectors';
import { KeyValueGrid } from '../../ui/primitives';
import type { StepProps } from './applyTypes';

export function IdentityStep({ form, update }: StepProps) {
  const [checking, setChecking] = useState(false);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!checking) return undefined;
    const timer = window.setInterval(() => setProgress((value) => Math.min(100, value + 25)), 350);
    return () => window.clearInterval(timer);
  }, [checking]);
  useEffect(() => {
    if (progress >= 100) {
      setChecking(false);
      update({ idVerified: true });
    }
  }, [progress, update]);
  if (form.idVerified) {
    return (
      <Alert icon={<VerifiedUserOutlinedIcon />} severity="success">
        Identity verified electronically for {form.contactName || 'you'}. Other people listed
        receive their own link.
      </Alert>
    );
  }
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        We verify identity electronically against official sources (GreenID). You&apos;ll need a
        driver licence or passport. It doesn&apos;t affect your credit score.
      </Typography>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <TextField
          label="Date of birth"
          type="date"
          InputLabelProps={{ shrink: true }}
          defaultValue="1978-04-12"
        />
        <TextField select label="Document" defaultValue="licence">
          <MenuItem value="licence">Driver licence</MenuItem>
          <MenuItem value="passport">Australian passport</MenuItem>
        </TextField>
        <TextField label="Document number" defaultValue="0123 4567" />
        <TextField select label="State of issue" defaultValue="VIC">
          {['VIC', 'NSW', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'].map((state) => (
            <MenuItem key={state} value={state}>
              {state}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      {checking ? (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 8, borderRadius: 4 }}
        />
      ) : (
        <Box>
          <Button variant="contained" onClick={() => setChecking(true)}>
            Verify my identity
          </Button>
        </Box>
      )}
    </Stack>
  );
}

export function TaxStep({ form, update }: StepProps) {
  return (
    <Stack spacing={2}>
      <TextField
        label="Tax file number (or ABN for companies)"
        value={form.tfn}
        onChange={(event) => update({ tfn: event.target.value })}
        helperText="Optional, but without it we must withhold 47% of distributions. Stored encrypted."
      />
      <TextField
        select
        label="Country of tax residence"
        value={form.residency}
        onChange={(event) => update({ residency: event.target.value })}
      >
        {[
          ['AU', 'Australia'],
          ['NZ', 'New Zealand'],
          ['SG', 'Singapore'],
          ['GB', 'United Kingdom'],
          ['US', 'United States'],
        ].map(([code, label]) => (
          <MenuItem key={code} value={code}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      <FormControlLabel
        control={
          <Checkbox
            checked={form.usPerson}
            onChange={(event) => update({ usPerson: event.target.checked })}
          />
        }
        label="I am (or the entity has) a US citizen or US tax resident (FATCA)"
      />
      {form.residency !== 'AU' && (
        <Alert severity="info">
          As a non-resident, interest distributions have 10% withholding and your details are
          reported under CRS.
        </Alert>
      )}
    </Stack>
  );
}

export function BankStep({ form, update }: StepProps) {
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Distributions are paid to this account. It must be in the investing entity&apos;s name.
      </Typography>
      <TextField
        label="Account name"
        value={form.bankName}
        onChange={(event) => update({ bankName: event.target.value })}
      />
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 2fr' }}>
        <TextField
          label="BSB"
          value={form.bsb}
          onChange={(event) => update({ bsb: event.target.value })}
        />
        <TextField
          label="Account number"
          value={form.account}
          onChange={(event) => update({ account: event.target.value })}
        />
      </Box>
    </Stack>
  );
}

export function EligibilityStep({ form, update }: StepProps) {
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        This fund is only for wholesale investors. Upload a certificate from a qualified accountant
        (valid for two years) or confirm another wholesale basis.
      </Typography>
      <Box
        component="label"
        sx={{
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: '6px',
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
        }}
      >
        <input
          type="file"
          hidden
          accept=".pdf,.jpg,.png"
          onChange={(event) => update({ certificate: event.target.files?.[0]?.name ?? '' })}
        />
        <CloudUploadOutlinedIcon color="action" />
        <Typography variant="body2" sx={{ mt: 1 }}>
          {form.certificate || "Upload accountant's certificate"}
        </Typography>
      </Box>
      <Box>
        <Button size="small" onClick={() => update({ certificate: 'accountant-certificate.pdf' })}>
          Use a sample file
        </Button>
      </Box>
    </Stack>
  );
}

export function ReviewStep({ form, update }: StepProps) {
  const scope = useInvestorScope();
  const fund = scope.funds.find((item) => item.id === form.fundId);
  const unitClass = scope.classes.find((item) => item.id === form.classId);
  return (
    <Stack spacing={2}>
      <KeyValueGrid
        items={[
          { label: 'Fund', value: `${fund?.name} · ${unitClass?.name}` },
          { label: 'Amount', value: money(Number(form.amount) || 0) },
          { label: 'Investing entity', value: form.applicantName },
          {
            label: 'People verified',
            value: form.owners.length ? `${form.owners.length} listed (links sent)` : 'You',
          },
          {
            label: 'TFN / ABN',
            value: form.tfn ? 'Provided' : 'Not provided (withholding applies)',
          },
          { label: 'Tax residency', value: form.residency },
          { label: 'Bank', value: `${form.bankName} · ${form.bsb}` },
          { label: 'Wholesale certificate', value: form.certificate || 'Missing' },
        ]}
      />
      <Divider />
      <Typography variant="body2" color="text.secondary">
        I have read the Information Memorandum and agree to be bound by the trust deed. I confirm
        the information given is true and that I am a wholesale client.
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={form.agree}
            onChange={(event) => update({ agree: event.target.checked })}
          />
        }
        label="I agree to the terms of the subscription agreement"
      />
      <TextField
        label="Sign by typing your full name"
        value={form.signature}
        onChange={(event) => update({ signature: event.target.value })}
      />
    </Stack>
  );
}
